import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from "@mui/material";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { formatAspectRatioLabel } from "./articleImageLayoutConstants";
import { getCroppedImageFile, readFileAsObjectUrl } from "./cropImageUtils";

const ArticleImageCropDialog = ({
  open,
  imageFile,
  aspectRatio,
  title,
  onCancel,
  onConfirm,
}) => {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!open || !imageFile) {
        setImageSrc("");
        return;
      }

      setLoadingPreview(true);
      setErrorMessage("");
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);

      try {
        const objectUrl = await readFileAsObjectUrl(imageFile);
        if (!cancelled) {
          setImageSrc(objectUrl);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error.message);
          setImageSrc("");
        }
      } finally {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [imageFile, open]);

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels || !imageFile) {
      setErrorMessage("Seleziona un'area di ritaglio valida.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      const croppedFile = await getCroppedImageFile(imageSrc, croppedAreaPixels, imageFile.name);
      await onConfirm(croppedFile);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Ritaglia l&apos;immagine in proporzione {formatAspectRatioLabel(aspectRatio)} prima del caricamento.
        </Typography>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 280, sm: 360 },
            bgcolor: "#0f172a",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          {loadingPreview ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <CircularProgress size={28} sx={{ color: "#fff" }} />
            </Box>
          ) : null}
          {!loadingPreview && imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          ) : null}
        </Box>
        <Box sx={{ mt: 2, px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_, value) => setZoom(value)}
            disabled={!imageSrc || submitting}
          />
        </Box>
        {errorMessage ? (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Annulla
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!imageSrc || submitting}
          sx={{ bgcolor: "#0b3d87" }}
        >
          {submitting ? "Caricamento..." : "Ritaglia e carica"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ArticleImageCropDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  imageFile: PropTypes.object,
  aspectRatio: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ArticleImageCropDialog;
